import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { BlueprintGrid } from "@/components/blueprint/Blueprint";
import { logoUrl } from "@/lib/assets";
import { APP_STORE_URL } from "@/lib/utils";

export function Footer() {
  return (
    <footer className="bp-footer" role="contentinfo">
      <BlueprintGrid light />
      <div className="bp-container py-12 px-5 md:px-8">
        <div className="grid gap-8 md:grid-cols-[1.2fr_0.8fr_0.8fr_0.8fr_0.8fr]">
          <div>
            <img src={logoUrl} alt="Quuil" width={170} height={68} className="bp-logo mb-4" />
            <p className="max-w-sm text-sm leading-6">
              A mobile design and merchandise platform for creating artwork, customizing real products, ordering securely, and discovering community designs.
            </p>
          </div>

          <FooterNav title="Explore">
            <Link to="/gallery">The Inkwell</Link>
            <Link to="/create">Create</Link>
            <Link to="/showcase">App Showcase</Link>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">Get the App</a>
          </FooterNav>

          <FooterNav title="Business">
            <Link to="/business">Bulk Orders</Link>
            <Link to="/business/catalog">Catalog</Link>
            <Link to="/business/quote">Get a Quote</Link>
          </FooterNav>

          <FooterNav title="Legal">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/support">Support</Link>
          </FooterNav>

          <FooterNav title="Social">
            <div className="flex gap-3" aria-label="Social media links">
              <SocialLink
                href="https://www.facebook.com/profile.php?id=61589081957671"
                label="Quuil on Facebook"
              >
                <FacebookIcon />
              </SocialLink>
              <SocialLink href="https://www.instagram.com/quuil_app/" label="Quuil on Instagram">
                <InstagramIcon />
              </SocialLink>
              <SocialLink label="Quuil on TikTok coming soon" disabled>
                <TikTokIcon />
              </SocialLink>
            </div>
          </FooterNav>
        </div>

        <div className="bp-footer-copyright mt-10 border-t border-[rgba(228,238,255,0.16)] pt-5 font-mono text-[0.68rem] uppercase tracking-[0.12em] text-[rgba(228,238,255,0.48)]">
          &copy; 2026 Quuil
        </div>
      </div>
    </footer>
  );
}

function SocialLink({
  href,
  label,
  disabled = false,
  children,
}: {
  href?: string;
  label: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  const className = [
    "inline-flex h-10 w-10 items-center justify-center border border-[rgba(228,238,255,0.18)] bg-[rgba(228,238,255,0.04)] text-[var(--bp-dim)] transition-colors",
    disabled
      ? "cursor-default opacity-45"
      : "hover:border-[var(--bp-cyan)] hover:text-[var(--bp-white)]",
  ].join(" ");

  if (disabled) {
    return (
      <span className={className} aria-label={label} aria-disabled="true" title="TikTok coming soon">
        {children}
      </span>
    );
  }

  return (
    <a className={className} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
      {children}
    </a>
  );
}

function FooterNav({ title, children }: { title: string; children: ReactNode }) {
  return (
    <nav className="grid content-start gap-3" aria-label={title}>
      <h4 className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-[var(--bp-cyan)]">{title}</h4>
      <div className="grid gap-2 text-sm text-[var(--bp-dim)] [&_a]:min-h-0 [&_a]:transition-colors hover:[&_a]:text-[var(--bp-white)]">
        {children}
      </div>
    </nav>
  );
}

function FacebookIcon() {
  return (
    <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M14.1 8.7h2.1V5.3c-.4 0-1.6-.1-3-.1-3 0-5 1.8-5 5.2v2.8H4.9V17h3.3v6h4v-6h3.3l.5-3.8h-3.8v-2.5c0-1.1.3-2 1.9-2Z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="4.5" />
      <circle cx="12" cy="12" r="3.2" />
      <circle cx="16.8" cy="7.2" r="0.8" fill="currentColor" stroke="none" />
    </svg>
  );
}

function TikTokIcon() {
  return (
    <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M14.4 4.5v9a4.8 4.8 0 1 1-4.8-4.8c.4 0 .8.1 1.1.2v3.2a1.7 1.7 0 1 0 1.1 1.6V4.5h2.6Z" />
      <path d="M14.4 4.5c.5 2.4 2 3.9 4.4 4.4V12c-1.7-.1-3.2-.7-4.4-1.7V4.5Z" />
    </svg>
  );
}
